import { Checkbox } from '@material-ui/core';
import {
  useCurrentRepairCapabilities,
  useSetRepairCapabilities,
  useSetRepairersActivity,
} from 'app/core/api/company/api-company';
import {
  PageDetails,
  RepairCapability,
} from 'app/core/api/company/api-company.types';
import { useProductCategories } from 'app/core/api/product/api-product';
import { ProductCategory } from 'app/core/api/product/api-product.types';
import { mergeApiErrors } from 'app/core/errors/error-utils';
import Button from 'app/shared/components/button/Button';
import FormAutocomplete from 'app/shared/components/form-input/FormAutocomplete';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import Icon from 'app/shared/components/icon/Icon';
import { OpenInSvg } from 'app/shared/components/icon/icon-lib';
import TreeSelectList, {
  DetailedGroup,
  groupChecked,
} from 'app/shared/components/list/TreeSelectList';
import {
  ErrorText,
  PaleOverlay,
  PromptUnsaved,
} from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import styledConsts from 'app/styles/styledConsts';
import styledMixins from 'app/styles/styledMixins';
import { isNonNullable } from 'app/tools/general.types';
import {
  useEffectOnce,
  useEffectOnChanges,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import styled, { css } from 'styled-components/macro';
import { useMovePage } from '../create-business-profile/business-profile-utils';
import BottomPageAndSubmitActions from '../create-business-profile/input-pages/components/BottomActions';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

type SelectableDetailedGroup = DetailedGroup<ProductCategory>;

const hasParent = <T extends ProductCategory>(
  input: T
): input is T & { parent: number } => !!input.parent;

type FormValues = {
  productAreas: ProductCategory[];
};

const ExpandButton = styled(Button)<{ $reverse: boolean }>`
  position: absolute;
  z-index: 2;
  top: 2px;
  right: 2px;
  transform: rotate(${p => (p.$reverse ? 180 : 0)}deg);
  background: white;
  svg {
    fill: ${styledConsts.cPrimary};
  }
`;

type ListBoxOpts = {
  $isEmpty: boolean;
  $fullScreen: boolean;
};
const ListBox = styled.div<ListBoxOpts>`
  ${styledMixins.flexCenter}
  background: white;
  z-index: 1;
  position: relative;
  flex: 100 1 auto;
  border: ${p => (p.$isEmpty ? 'none' : 'solid')};
  border-width: ${p => (p.$fullScreen ? 2 : 1)}px;
  border-color: ${p =>
    p.$fullScreen ? styledConsts.cPrimary : 'lightgray'};
  ${styledMixins.roundCorners}
  overflow: hidden;
  ${ExpandButton} {
    display: ${p => (p.$isEmpty ? 'none' : 'flex')};
  }
  ${p =>
    p.$fullScreen &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
    `}
`;

const API_CATEGORIES_FIELD_NAME = 'repairCategoryIds';

// NOTE: *** 'current' is used to mean 'current backend status' ***

type UseCatGroupsProps = {
  currentCapabilities?: RepairCapability[];
  allCategories?: ProductCategory[];
};
type CatGroups = {
  allAreas: ProductCategory[];
  defaultValues: {
    productAreas: ProductCategory[];
  };
  childlessGroupIds: number[];
  allGroupsCurrentDetails: SelectableDetailedGroup[];
};
const emptyArr: never[] = [];
export const useCategorySections = ({
  currentCapabilities = emptyArr,
  allCategories: allCatgs = emptyArr,
}: UseCatGroupsProps): CatGroups =>
  useMemo(() => {
    const allAreas = allCatgs.filter(c => !c.parent);
    const allAreaIds = _.map(allAreas, 'id');
    const allGroups = allCatgs.filter(c =>
      allAreaIds.includes(c?.parent || -1)
    );
    const allGroupIds = _.map(allGroups, 'id');
    const allVariants = allCatgs.filter(c =>
      allGroupIds.includes(c?.parent || -1)
    );
    const allVariantsParents = _.map(allVariants, 'parent');
    const childlessGroupIds = _(allGroups)
      .filter(g => !allVariantsParents.includes(g.id))
      .map('id')
      .value();

    const currentCategories = currentCapabilities
      // NOTE: remove broken product categories from old system
      ?.filter(capability => !!capability.productCategory)
      .map(capability => capability.productCategoryDetails);

    const getCurrent = (
      level: 'areas' | 'groups' | 'variants'
    ): ProductCategory[] =>
      _.intersectionBy(
        currentCategories,
        { areas: allAreas, groups: allGroups, variants: allVariants }[
          level
        ],
        'id'
      );

    // NOTE: this holds the list of areas that are 'used', aka are
    // selected or have at least one of their children (group-level)
    // or grandchildren (variant-level) selected
    const currentAreasUsed = _([] as ProductCategory[])
      .concat(getCurrent('variants'))
      .map(v => v.parent) // all parents of variants = all groups 'used'
      .uniq()
      .map(gId => allGroups.find(g => g.id === gId))
      .concat(getCurrent('groups')) // add groups from current
      .map(g => g?.parent) // all parents of groups = all areas 'used'
      .uniq()
      .map(aId => allAreas.find(a => a.id === aId))
      .concat(getCurrent('areas')) // add areas from current
      .uniqBy('id')
      .filter(isNonNullable)
      .value();

    // list of all the ids of variants currently selected
    const currentVariantIds = getCurrent('variants').map(v => v.id);
    // list of selected (childless) groups ids - a group can only be
    // 'selected' directly if it has no variants (aka childless).
    const currentChildlessGroupIds = getCurrent('groups')
      .filter(g => childlessGroupIds.includes(g.id))
      .map(g => g.id);

    // NOTE: contains *all* the groups, and adds a 'variants' field
    // on each to store the 'selected' state of each group's variants.
    // This list is filtered later to get just the available groups
    // (aka the ones with parents in the selected areas list).
    const allGroupsCurrentDetails: SelectableDetailedGroup[] = _(
      allGroups
    )
      .filter(hasParent)
      .map(eachGrp => ({
        ...eachGrp,
        childlessAndSelected: currentChildlessGroupIds.includes(
          eachGrp.id
        ),
        // get all variants on the group, and fill out 'selected' field
        variants: _(allVariants)
          .filter(eachVrnt => eachVrnt.parent === eachGrp.id) // only variants on this group
          .sortBy('name')
          .map(eachVrnt => ({
            ...eachVrnt,
            selected: currentVariantIds.includes(eachVrnt.id),
          }))
          .value(),
      }))
      .value();

    const defaultValues = { productAreas: currentAreasUsed };

    return {
      allAreas,
      defaultValues,
      childlessGroupIds,
      allGroupsCurrentDetails,
    };
  }, [allCatgs, currentCapabilities]);

type Props = {
  firstTime?: boolean;
  isIndividual?: boolean;
};
export default function UpdateRepairCapabilities({
  firstTime,
  isIndividual,
}: Props): JSX.Element {
  useSetPageTitle('Repair Categories');

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Repair Categories',
    };
    repairActivity(pageDetails).subscribe();
  });
  const history = useHistory();
  const { showSnack } = useShowInfo();
  const { nextPage } = useMovePage();

  const editable$ = useMemo(
    () => new BehaviorSubject(!!firstTime),
    [firstTime]
  );
  const editable = useObservableEagerState(editable$);
  const [listFullscreen, setListFullscreen] = useState(false);

  const {
    request$: setRepairCapabilities$,
    sending: submittingCapabilities,
    result: submitted,
    error: rawApiError,
  } = useSetRepairCapabilities({
    fresh: true,
  });

  // NOTE: this is needed as the api and form field structure differ
  const apiError = useMemo(() => {
    const fieldError = rawApiError?.[API_CATEGORIES_FIELD_NAME];
    const unFieldedError = fieldError && { detail: fieldError };
    return mergeApiErrors([rawApiError, unFieldedError || {}]);
  }, [rawApiError]);

  const { resource: allCategories } = useProductCategories();
  const {
    resource: currentCapabilities,
    loadingMissing,
    loading,
  } = useCurrentRepairCapabilities({ fresh: true });

  const {
    allAreas,
    defaultValues,
    childlessGroupIds,
    allGroupsCurrentDetails,
  } = useCategorySections({
    currentCapabilities,
    allCategories,
  });

  const [allGroupsDetailed, setAllGroupsDetailed] = useState<
    SelectableDetailedGroup[]
  >([]);
  useEffect(() => {
    // set default (starting) value when loaded
    setAllGroupsDetailed(allGroupsCurrentDetails);
  }, [allGroupsCurrentDetails]);

  const { handleSubmit, inputProps, watch } =
    useInputForm<FormValues>({ defaultValues });

  const chosenAreas = watch('productAreas');

  const info = useMemo(() => {
    const chosenAreasIds = chosenAreas.map(a => a.id);
    const availableGroupsDetailed = allGroupsDetailed.filter(g =>
      chosenAreasIds.includes(g.parent || -1)
    );
    const chosenVariantIds = availableGroupsDetailed
      .map(g => g.variants.filter(v => v.selected).map(v => v.id))
      .flat();
    const chosenChildlessGroupIds = availableGroupsDetailed
      .filter(g => g.childlessAndSelected)
      .map(g => g.id);
    const allChosenIds = chosenAreasIds.concat(
      chosenVariantIds,
      chosenChildlessGroupIds
    );

    const availableGroupsDetailedCollected = _(
      availableGroupsDetailed
    )
      .groupBy(
        g =>
          allAreas.find(a => a.id === g.parent)?.displayName ||
          'Miscellaneous'
      )
      .entries()
      .map(([k, v]) => ({ area: k, groups: v }))
      .sortBy('area')
      .map(({ area, groups }) => ({
        area,
        groups: _.sortBy(groups, 'priority'),
      }))
      .value();

    return { availableGroupsDetailedCollected, allChosenIds };
  }, [chosenAreas, allGroupsDetailed, allAreas]);
  const { availableGroupsDetailedCollected, allChosenIds } = info;

  const submit = () => {
    setRepairCapabilities$({
      [API_CATEGORIES_FIELD_NAME]: allChosenIds,
    }).subscribe(() => {
      showSnack({
        message: 'Updated successfully!',
      });
      editable$.next(false);
      if (firstTime) nextPage(isIndividual);
    });
  };

  const handleGroupClick =
    (groupClicked: SelectableDetailedGroup) => () => {
      const selected = !groupChecked(groupClicked);
      const markVariants = (variants: ProductCategory[]) =>
        variants.map(v => ({ ...v, selected }));
      setAllGroupsDetailed(allGroups =>
        allGroups.map(eachGrp => {
          if (eachGrp.id !== groupClicked.id) return eachGrp;
          return childlessGroupIds.includes(eachGrp.id)
            ? { ...eachGrp, childlessAndSelected: selected }
            : {
                ...eachGrp,
                variants: markVariants(eachGrp.variants),
              };
        })
      );
    };

  const handleVariantClick = (vrntClicked: ProductCategory) => () => {
    setAllGroupsDetailed(allGroups =>
      allGroups.map(eachGrp => ({
        ...eachGrp,
        variants: eachGrp.variants.map(eachVrnt => {
          if (eachVrnt.id !== vrntClicked.id) return eachVrnt;
          return { ...eachVrnt, selected: !eachVrnt.selected };
        }),
      }))
    );
  };

  // Click cancel button -> get navigated back automatically
  useEffectOnChanges(() => {
    if (!editable && !submittingCapabilities && !rawApiError) {
      history.goBack();
    }
  }, [editable, rawApiError, history, submittingCapabilities]);

  const groupsListEmpty = _.isEmpty(availableGroupsDetailedCollected);

  return (
    <>
      <Form flexStretch onSubmit={handleSubmit(submit)}>
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Choose the product categories you repair
        </Body>
        <FormAutocomplete
          controlName="productAreas"
          options={allAreas}
          getOptionLabel={option => option.displayName}
          disableCloseOnSelect
          disableClearIcon
          disabled={!currentCapabilities || !editable}
          autoHighlight
          multiple
          getOptionSelected={(opt, val) => opt.id === val.id}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.displayName}
            </>
          )}
          TextInputProps={renderProps => {
            const selected = renderProps.InputProps.startAdornment;
            const anySelected =
              _.isArray(selected) && selected.length;
            return {
              placeholder: anySelected
                ? `${anySelected} selected. Add more..`
                : 'Choose areas',
              label: 'Repair categories',
              greyPlaceholder: true,
              // TODO: Improve. Atm, Autocomplete has a default 9px padding around input.
              minHeight: 57 - 9 * 2,
            };
          }}
          {...inputProps}
        />
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Select your specific repair categories
          {!groupsListEmpty && (
            <>
              <br />
              (expand list to see more)
            </>
          )}
        </Body>

        <ListBox
          $fullScreen={listFullscreen}
          $isEmpty={groupsListEmpty}
        >
          {!editable && <PaleOverlay />}
          <ExpandButton
            iconButton={{ size: 'small' }}
            $reverse={listFullscreen}
            onClick={() => setListFullscreen(s => !s)}
          >
            <Icon imgNode={<OpenInSvg />} />
          </ExpandButton>
          <TreeSelectList
            {...{
              availableGroupsDetailedCollected,
              childlessGroupIds,
              handleGroupClick,
              handleVariantClick,
            }}
          />
        </ListBox>

        <Body tiny>Asterisks (*) indicate required fields</Body>
        <ErrorText message={apiError} />
        {firstTime ? (
          <BottomPageAndSubmitActions
            isSubmit
            isIndividual={isIndividual}
          />
        ) : (
          <BottomEditActions
            sending={submittingCapabilities}
            {...{ loading, editable$ }}
          />
        )}
        <PromptUnsaved
          when={
            !submitted &&
            !loadingMissing &&
            editable &&
            !_.isEqual(
              _(currentCapabilities).map('productCategory').sort(),
              _(allChosenIds).sort()
            )
          }
        />
      </Form>
    </>
  );
}
